/**
 * The pure half of `TokenInput`: what a token looks like, where the caret is
 * inside one, and how one is inserted. No DOM here, so every rule is unit
 * testable and the same rule paints `TokenText` and drives the picker.
 *
 * A token is `{{…}}`: an attribute such as `{{contact.name}}` or a flow
 * variable such as `{{order_number}}`, with whatever fallback grammar the
 * service on the other end reads (`|there` for broadcasts and compose,
 * `?? there` for flows). Meta's own positional slots, `{{1}}`, are digits
 * inside the same braces: they are painted differently and never offered,
 * because they are the template's numbering rather than something the person
 * chose.
 */

export interface TokenSuggestion {
  /** What goes between the braces: `contact.name`, `contact.fields.city`, `order_number`. */
  token: string;
  /** What the person reads in the list. */
  label: string;
  /** Heading the suggestion is listed under. Suggestions with the same group sit together. */
  group?: string;
  /** One line under the label, for when the token needs explaining. */
  description?: string;
}

export const DEFAULT_TRIGGER = '{{';
export const DEFAULT_CLOSING = '}}';

export type TokenSegment =
  | { kind: 'text'; text: string }
  | { kind: 'token'; text: string; slot: boolean };

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** True when the token is one of Meta's numbered slots: `{{1}}`, `{{ 2 }}`. */
export function isSlotToken(tokenText: string, trigger = DEFAULT_TRIGGER, closing = DEFAULT_CLOSING): boolean {
  if (!tokenText.startsWith(trigger) || !tokenText.endsWith(closing)) return false;
  const inner = tokenText.slice(trigger.length, tokenText.length - closing.length).trim();
  return /^\d+$/.test(inner);
}

/**
 * A value cut into plain text and tokens, in order. A token runs from a
 * trigger to the next closing on the same line; an unclosed trigger is plain
 * text, which is what the person sees while they are still typing it.
 */
export function segmentTokens(value: string, trigger = DEFAULT_TRIGGER, closing = DEFAULT_CLOSING): TokenSegment[] {
  const out: TokenSegment[] = [];
  if (!value) return out;
  const re = new RegExp(`${escapeRegex(trigger)}[^{}\\n]*?${escapeRegex(closing)}`, 'g');
  let last = 0;
  for (const m of value.matchAll(re)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'text', text: value.slice(last, at) });
    out.push({ kind: 'token', text: m[0], slot: isSlotToken(m[0], trigger, closing) });
    last = at + m[0].length;
  }
  if (last < value.length) out.push({ kind: 'text', text: value.slice(last) });
  return out;
}

export interface OpenToken {
  /** Index of the trigger's first character. */
  start: number;
  /** What has been typed after the trigger, up to the caret. */
  query: string;
}

/**
 * Whether the caret sits inside a token that is still being typed: after a
 * trigger with no closing between it and the caret, on the same line. The
 * query is what follows the trigger, so `{{con|` (caret at the bar) opens the
 * list filtered to "con"; `{{contact.name}}|` is closed, and so is
 * `{{contact.name|there` once a fallback has been started.
 */
export function findOpenToken(
  value: string,
  caret: number,
  trigger = DEFAULT_TRIGGER,
  closing = DEFAULT_CLOSING
): OpenToken | null {
  if (caret < trigger.length) return null;
  const before = value.slice(0, caret);
  const start = before.lastIndexOf(trigger);
  if (start === -1) return null;
  const between = before.slice(start + trigger.length);
  if (between.includes(closing) || between.includes('}') || between.includes('\n')) return null;
  if (between.includes('|') || between.includes('?')) return null;
  return { start, query: between };
}

/** The suggestions whose token or label contains the query, case-insensitively; all of them for an empty query. */
export function filterSuggestions(list: readonly TokenSuggestion[], query: string): TokenSuggestion[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...list];
  const hits = list.filter((s) => s.token.toLowerCase().includes(q) || s.label.toLowerCase().includes(q));
  // Whatever starts with the query comes first, so typing "na" puts Name at the top.
  const starts = hits.filter((s) => s.token.toLowerCase().startsWith(q) || s.label.toLowerCase().startsWith(q));
  return [...starts, ...hits.filter((s) => !starts.includes(s))];
}

/** The visible suggestions bucketed under their group, in order of first appearance. */
export function groupSuggestions(list: readonly TokenSuggestion[]): Array<{ group: string | undefined; items: Array<{ item: TokenSuggestion; index: number }> }> {
  const order: Array<string | undefined> = [];
  const buckets = new Map<string | undefined, Array<{ item: TokenSuggestion; index: number }>>();
  list.forEach((item, index) => {
    const key = item.group;
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    buckets.get(key)!.push({ item, index });
  });
  return order.map((group) => ({ group, items: buckets.get(group)! }));
}

/**
 * The value with `{{token}}` written over the open token, and where the caret
 * belongs afterwards: just after the closing braces. Closing braces the person
 * already typed ahead of the caret are consumed rather than doubled.
 */
export function insertToken(
  value: string,
  start: number,
  caret: number,
  token: string,
  trigger = DEFAULT_TRIGGER,
  closing = DEFAULT_CLOSING
): { value: string; caret: number } {
  let end = caret;
  const ahead = value.slice(caret);
  const trimmedAhead = ahead.replace(/^\s+/, '');
  if (trimmedAhead.startsWith(closing)) end = caret + (ahead.length - trimmedAhead.length) + closing.length;
  const written = `${trigger}${token}${closing}`;
  const next = value.slice(0, start) + written + value.slice(end);
  return { value: next, caret: start + written.length };
}
